import React from "react";
import InputMask from "react-input-mask";
import { ClipLoader } from "react-spinners";
import { biddingConfig } from "../user-page/user-pages/funds/FundsData";

function calculatePurchaseLimit(biddingLimit, thresholds) {
  const sortedThresholds = [...thresholds].sort(
    (a, b) => b.minBidding - a.minBidding
  );
  const threshold = sortedThresholds.find((t) => biddingLimit >= t.minBidding);
  return threshold ? threshold.vehicles : 0;
}

const PaymentForm = ({
  paymentDetails,
  handleInputChange,
  errors,
  onSubmit,
  loading,
  closeModal,
  cardAmount,
  showDepositDropdown = false,
}) => {
  const depositOptions = [
    { value: 350, label: '$350', type: 'local' },
    { value: 750, label: '$750', type: 'import' },
    { value: 1500, label: '$1,500', type: 'import' },
    { value: 2000, label: '$2,000', type: 'import' },
    { value: 2500, label: '$2,500', type: 'import' },
    { value: 3000, label: '$3,000', type: 'import' },
    { value: 3500, label: '$3,500', type: 'import' },
    { value: 4000, label: '$4,000', type: 'import' },

  ];

  // Add custom amount option if the calculated amount doesn't match any preset
  const currentAmount = Number(paymentDetails.card_amount);
  const hasCustomAmount = currentAmount && !depositOptions.find(opt => opt.value === currentAmount);
  const allOptions = hasCustomAmount 
    ? [...depositOptions, { value: currentAmount, label: `$${currentAmount.toLocaleString()}`, type: 'custom' }]
    : depositOptions;

  const handleDepositSelect = (e) => {
    const value = e.target.value;
    const numericValue = value ? String(value) : '';
    const selected = allOptions.find((o) => String(o.value) === String(value));
    // Update card_amount
    handleInputChange({ target: { name: 'card_amount', value: numericValue } });
    // Mirror to deposit for backward compatibility
    handleInputChange({ target: { name: 'deposit', value: numericValue } });
    // Also send depositType for backend clarity
    if (selected) {
      handleInputChange({ target: { name: 'depositType', value: selected.type } });
    }
  };

  const renderDepositInfo = () => {
    const amount = Number(paymentDetails.card_amount);
    if (!amount) return null;
    const depositPct = biddingConfig?.depositPercentage || 0.1;
    const biddingLimit = Math.floor(amount / depositPct); 
    const thresholds = biddingConfig?.purchaseLimitThresholds || [];
    const vehicles = calculatePurchaseLimit(biddingLimit, thresholds);
    return (
      <p className="text-sm text-gray-600 mt-2 font-semibold">
        {amount < 750
          ? `You can bid on ${vehicles} local car${vehicles === 1 ? '' : 's'}, with a bidding limit up to $${biddingLimit.toLocaleString()}.
          For imported vehicles (IAAI/Copart), a minimum deposit of $750 is required.`
          : `You can place upto ${vehicles} bids, with a bidding limit up to $${biddingLimit.toLocaleString()}.`}
      </p>
    );
  };
  return (
    <form onSubmit={onSubmit}>
      {/* Cardholder Name */}
      <div className="mb-4">
        <label className="block font-semibold">Cardholder Name</label>
        <input
          type="text"
          name="card_name"
          value={paymentDetails.card_name}
          onChange={handleInputChange}
          placeholder="Enter cardholder name"
          className="border rounded p-2 w-full"
        />
        {errors.card_name && (
          <p className="text-red-500 text-sm">{errors.card_name}</p>
        )}
      </div>
      {/* Card Number */}
      <div className="mb-4">
        <label className="block font-semibold">Card Number</label>
        <InputMask
          mask="9999 9999 9999 9999"
          name="card_number"
          value={paymentDetails.card_number}
          onChange={handleInputChange}
          placeholder="1234 5678 9012 3456"
          className="border rounded p-2 w-full"
        />
        {errors.card_number && (
          <p className="text-red-500 text-sm">{errors.card_number}</p>
        )}
      </div>
      {/* Card CVV */}
      <div className="mb-4">
        <label className="block font-semibold">Card CVV</label>
        <InputMask
          mask="999"
          name="card_cvv"
          value={paymentDetails.card_cvv}
          onChange={handleInputChange}
          placeholder="123"
          className="border rounded p-2 w-full"
        />
        {errors.card_cvv && (
          <p className="text-red-500 text-sm">{errors.card_cvv}</p>
        )}
      </div>

      {/* Card Expiration */}
      <div className="mb-4">
        <label className="block font-semibold">Card Expiration (MM/YY)</label>
        <InputMask
          mask="99/99"
          name="card_exp"
          value={paymentDetails.card_exp}
          onChange={handleInputChange}
          placeholder="MM/YY"
          className="border rounded p-2 w-full"
        />
        {errors.card_exp && (
          <p className="text-red-500 text-sm">{errors.card_exp}</p>
        )}
      </div>

      {/* Deposit Amount */}
      <div className="mb-4">
        <label className="block font-semibold" title="US Dollar">
          Deposit Amount (BSD)
        </label>
        {showDepositDropdown ? (
          <>
            <select
              name="card_amount"
              value={paymentDetails.card_amount || ''}
              onChange={handleDepositSelect}
              required
              className="border rounded p-2 w-full bg-white "
            >
              <option value="" disabled>
                Select a deposit amount
              </option>
              {allOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {renderDepositInfo()}
          </>
        ) : (
          <input
            type="text"
            name="card_amount"
            value={paymentDetails.card_amount}
            onChange={handleInputChange}
            disabled={paymentDetails.card_amount}
            placeholder="Enter deposit amount"
            className="border rounded p-2 w-full"
            min="1"
          />
        )}
        {errors.card_amount && (
          <p className="text-red-500 text-sm">{errors.card_amount}</p>
        )}
      </div>

      {/* Email */}
      <div className="mb-4">
        <label className="block font-semibold">Email</label>
        <input
          type="email"
          name="email"
          value={paymentDetails.email}
          onChange={handleInputChange}
          placeholder="Enter your email"
          className="border rounded p-2 w-full"
        />
        {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
      </div>

      {/* Payment Purpose (Disabled) */}
      <div className="mb-4">
        <label className="block font-semibold">Payment Purpose</label>
        <input
          type="text"
          name="payment_purpose"
          value={paymentDetails.paymentPurpose}
          disabled
          className="border rounded p-2 w-full bg-gray-200"
        />
      </div>

      {/* Submit Button */}
      {/* <button
        type="submit"
        className="w-full bg-primary-red text-white py-2 rounded font-semibold mt-4"
        disabled={loading} // Disable button when loading
      >
        {loading ? <ClipLoader size={20} color={"#fff"} /> : "Submit Payment"}
      </button>

      <button
        type="button"
        onClick={closeModal}
        className="w-full sm:w-1/2 bg-gray-300 text-gray-800 py-2 rounded font-semibold hover:bg-gray-400 transition"
      >
        Close
      </button> */}

      {/* Action Buttons */}
<div className="flex flex-col sm:flex-row gap-4 mt-6">
  {/* Close Button */}
  

  {/* Submit Button */}
  <button
    type="submit"
    className="w-full sm:w-1/2 bg-primary-red text-white py-2 rounded font-semibold hover:bg-red-700 transition"
    disabled={loading}
  >
    {loading ? <ClipLoader size={20} color={"#fff"} /> : "Submit Payment"}
  </button>

  <button
    type="button"
    onClick={closeModal}
    className="w-full sm:w-1/2 bg-gray-300 text-gray-800 py-2 rounded font-semibold hover:bg-gray-400 transition"
  >
    Close
  </button>
</div>

    </form>
  );
};

export default PaymentForm;
